/**
 * Online systems are optional. Local play uses LocalOnlineGameService against
 * LocalProfileStore — banked profile persistence works without Cloudflare.
 * See docs/CLOUDFLARE_DEPLOYMENT.md and docs/ONLINE_PATH.md for the CF path.
 */

import type { BankedProfile } from "../simulation/persistence";
import type { LocalProfileStore } from "./local-profile";
import { validateScoreSubmission, type ScoreTier } from "./score-validation";
import { LeaderboardPanel } from "../presentation/leaderboard";

export const ZONEVELOCITY_GAME_ID = "zonevelocity";

export type PlayerIdentity =
  { kind: "anonymous"; id: string } | { kind: "account"; id: string; username: string };

export type ScoreSubmission = {
  gameId: string;
  score: number;
  occurredAt: string;
  metadata?: Record<string, string | number | boolean>;
};

/** Banked profile shape persisted server-side (D1) or locally. */
export type RemoteBankedProfile = {
  playerId: string;
  salvage: number;
  unlockedFrames: string[];
  unlockedWeapons: string[];
  weaponCalibration: BankedProfile["weaponCalibration"];
  queuedSortieContract: BankedProfile["queuedSortieContract"];
  cosmetics: string[];
  blueprints: string[];
  runsCompleted: number;
  runsFailed: number;
  selectedFrameId: string;
  selectedTrailId: string;
  updatedAt: string;
};

export type BankedRewardClaim = {
  gameId: string;
  playerId: string;
  runId: string;
  salvageDelta: number;
  blueprintIds: string[];
  cosmeticIds: string[];
  clientTicket: string;
};

export type LeaderboardEntry = {
  playerId: string;
  score: number;
  tier: ScoreTier;
  occurredAt: string;
  metadata?: Record<string, string | number | boolean>;
};

export interface OnlineGameService {
  getIdentity(): Promise<PlayerIdentity>;
  register(username: string, password: string): Promise<PlayerIdentity>;
  signIn(username: string, password: string): Promise<PlayerIdentity>;
  submitScore(submission: ScoreSubmission): Promise<void>;
  getLeaderboard(limit?: number): Promise<LeaderboardEntry[]>;
  getBankedProfile(): Promise<RemoteBankedProfile | null>;
  claimBankedRewards(claim: BankedRewardClaim): Promise<RemoteBankedProfile>;
}

function toRemote(playerId: string, profile: BankedProfile): RemoteBankedProfile {
  return {
    playerId,
    salvage: profile.salvage,
    unlockedFrames: [...profile.unlockedFrames],
    unlockedWeapons: [...profile.unlockedWeapons],
    weaponCalibration: structuredClone(profile.weaponCalibration),
    queuedSortieContract: profile.queuedSortieContract,
    cosmetics: [...profile.cosmetics],
    blueprints: [...profile.blueprints],
    runsCompleted: profile.runsCompleted,
    runsFailed: profile.runsFailed,
    selectedFrameId: profile.selectedFrameId,
    selectedTrailId: profile.selectedTrailId,
    updatedAt: new Date(0).toISOString(),
  };
}

/**
 * Local online port: identity + banked profile read/write via LocalProfileStore.
 * Rejects duplicate reward tickets; applies valid claims to banked salvage/unlocks.
 */
export class LocalOnlineGameService implements OnlineGameService {
  private identity: PlayerIdentity;
  private readonly spentTickets = new Set<string>();

  public constructor(
    identity: PlayerIdentity,
    private readonly store: LocalProfileStore,
  ) {
    this.identity = identity;
  }

  public async getIdentity(): Promise<PlayerIdentity> {
    return this.identity;
  }

  public async register(username: string, password: string): Promise<PlayerIdentity> {
    if (!username.trim() || password.length < 4) {
      throw new Error("Username and password (4+ chars) required");
    }
    this.identity = { kind: "account", id: this.identity.id, username: username.trim() };
    return this.identity;
  }

  public async signIn(username: string, password: string): Promise<PlayerIdentity> {
    if (!username.trim() || password.length < 4) {
      throw new Error("Invalid credentials");
    }
    this.identity = { kind: "account", id: this.identity.id, username: username.trim() };
    return this.identity;
  }

  public async submitScore(submission: ScoreSubmission): Promise<void> {
    if (submission.gameId !== ZONEVELOCITY_GAME_ID || submission.score < 0) {
      throw new Error("Invalid score submission");
    }
    const meta = submission.metadata ?? {};
    const check = validateScoreSubmission({
      score: submission.score,
      level: Number(meta.level ?? 1),
      simTime: Number(meta.simTime ?? 0),
      bestStreak: Number(meta.bestStreak ?? 0),
      totalKills: Number(meta.totalKills ?? 0),
    });
    if (!check.ok) throw new Error(check.reason);
    LeaderboardPanel.pushLocal(this.identity.id, submission.score, meta);
  }

  public async getLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
    return LeaderboardPanel.loadLocal().slice(0, limit);
  }

  public async getBankedProfile(): Promise<RemoteBankedProfile | null> {
    const profile = this.store.loadBanked();
    return toRemote(this.identity.id, profile);
  }

  public async claimBankedRewards(claim: BankedRewardClaim): Promise<RemoteBankedProfile> {
    if (claim.gameId !== ZONEVELOCITY_GAME_ID) throw new Error("Invalid gameId");
    if (claim.playerId !== this.identity.id) throw new Error("Unauthorized");
    if (claim.salvageDelta < 0) throw new Error("Negative salvage rejected");
    if (this.spentTickets.has(claim.clientTicket)) throw new Error("Duplicate reward ticket");

    const profile = this.store.loadBanked();
    profile.salvage += claim.salvageDelta;
    for (const bp of claim.blueprintIds) {
      if (!profile.blueprints.includes(bp)) profile.blueprints.push(bp);
    }
    for (const cosmetic of claim.cosmeticIds) {
      if (!profile.cosmetics.includes(cosmetic)) profile.cosmetics.push(cosmetic);
    }
    this.store.saveBanked(profile);
    this.spentTickets.add(claim.clientTicket);
    return toRemote(this.identity.id, profile);
  }
}
